import { createContext, use, useState, ReactNode } from "react";

type VehicleCompareContextType = {
  ids: string[];
  addId: (id: string) => void;
  removeId: (id: string) => void;
  toggleId: (id: string) => void;
  setIds: (ids: string[]) => void;
  clearIds: () => void;
};

export const VehicleCompareContext = createContext<VehicleCompareContextType | null>(null);

type VehicleCompareProviderProps = {
  children: ReactNode;
  defaultValue?: string[];
};

export const VehicleCompareProvider = ({ children, defaultValue }: VehicleCompareProviderProps) => {
  const [ids, setIdsState] = useState<string[]>(defaultValue || []);

  const addId = (id: string) => {
    setIdsState((prevIds) => {
      if (!prevIds.includes(id)) {
        return [...prevIds, id];
      }
      return prevIds;
    });
  };

  const removeId = (id: string) => {
    setIdsState((prevIds) => prevIds.filter((existingId) => existingId !== id));
  };

  const toggleId = (id: string) => {
    setIdsState((prevIds) => {
      if (prevIds.includes(id)) {
        return prevIds.filter((existingId) => existingId !== id);
      }
      return [...prevIds, id];
    });
  };

  const setIds = (newIds: string[]) => {
    setIdsState(newIds);
  };

  const clearIds = () => {
    setIdsState([]);
  };

  const value: VehicleCompareContextType = {
    ids,
    addId,
    removeId,
    toggleId,
    setIds,
    clearIds,
  };

  return (
    <VehicleCompareContext.Provider value={value}>
      {children}
    </VehicleCompareContext.Provider>
  );
};

const useVehicleCompare = () => {
  const context = use(VehicleCompareContext);

  if (!context) {
    throw new Error('useVehicleCompare must be used within a VehicleCompareProvider');
  }

  return context;
}

export default useVehicleCompare;